import type { FirstClassDocument, Highlight, PartialDocument } from '../../types';
import { getCopyWordLimitForDocument } from '../../types/bookstore';
import { truncateHtml } from '../../utils/truncateHtml';
import { truncateMarkdown } from '../../utils/truncateMarkdown';
import { truncatePlainText } from '../../utils/truncateText';

const DRM_RESTRICTION_MESSAGE = 'Limited due to content restrictions';

// Display functions add the restriction message at render time, not storage time.
// This is required because the Renderer validates stored content as a prefix of the DOM text.
function appendRestrictionMessage(
  content: string | undefined,
  isDrmTruncated: boolean | undefined,
  suffix: string,
): string {
  if (isDrmTruncated && content) {
    return `${content}${suffix}`;
  }
  return content ?? '';
}

export function getHighlightContentForDisplay(highlight: Highlight): string {
  return appendRestrictionMessage(
    highlight.content,
    highlight.source_specific_data?.drm_truncated,
    `\n\n[${DRM_RESTRICTION_MESSAGE}]`,
  );
}

export function getHighlightHtmlForDisplay(highlight: Highlight): string {
  return appendRestrictionMessage(
    highlight.html,
    highlight.source_specific_data?.drm_truncated,
    `<p><em>[${DRM_RESTRICTION_MESSAGE}]</em></p>`,
  );
}

export function getHighlightMarkdownForDisplay(highlight: Highlight): string {
  return appendRestrictionMessage(
    highlight.markdown,
    highlight.source_specific_data?.drm_truncated,
    `\n\n*[${DRM_RESTRICTION_MESSAGE}]*`,
  );
}

export interface DRMTruncationResult {
  content: string;
  html: string;
  markdown: string;
  wasTruncated: boolean;
  originalWordCount: number;
  truncatedWordCount: number;
}

export function applyDRMTruncationIfNeeded(
  content: { text: string; html: string; markdown: string; },
  parentDoc: PartialDocument<FirstClassDocument, 'source_specific_data' | 'language'> | null,
): DRMTruncationResult {
  const wordLimit = getCopyWordLimitForDocument(parentDoc);

  if (!wordLimit) {
    return {
      content: content.text,
      html: content.html,
      markdown: content.markdown,
      wasTruncated: false,
      originalWordCount: 0,
      truncatedWordCount: 0,
    };
  }

  const language = parentDoc?.language;

  const truncatedContent = truncatePlainText(content.text, wordLimit, language);
  const truncatedHtml = truncateHtml(content.html, wordLimit, language);
  const truncatedMarkdown = truncateMarkdown(content.markdown, wordLimit, language);

  const wasTruncated = truncatedContent.wasTruncated ||
                       truncatedHtml.wasTruncated ||
                       truncatedMarkdown.wasTruncated;

  return {
    content: truncatedContent.text,
    html: truncatedHtml.text,
    markdown: truncatedMarkdown.text,
    wasTruncated,
    originalWordCount: truncatedContent.originalWordCount,
    truncatedWordCount: truncatedContent.truncatedWordCount,
  };
}
